import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Refresh as RefreshIcon, PlayArrow as StartIcon, Stop as StopIcon } from '@mui/icons-material';
import { getAsgStatus, updateAsg, getInstanceTypes, InstanceType, AsgStatus } from '../api/videoApi';

export default function AsgDashboardComponent() {
  const [asgStatus, setAsgStatus] = useState<AsgStatus | null>(null);
  const [instanceTypes, setInstanceTypes] = useState<InstanceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [desiredCapacity, setDesiredCapacity] = useState<number>(0);
  const [selectedInstanceType, setSelectedInstanceType] = useState<string>('');

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const [status, types] = await Promise.all([getAsgStatus(), getInstanceTypes()]);
      setAsgStatus(status);
      setInstanceTypes(types);
      setDesiredCapacity(status.desired_capacity);
      setSelectedInstanceType(status.instance_type || types[0]?.name || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ASG status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // Refresh every 30 seconds
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleUpdate = async (capacityOverride?: number) => {
    try {
      setUpdating(true);
      setError(null);
      setSuccess(null);

      const capacity = capacityOverride ?? desiredCapacity;
      const updateRequest: { desired_capacity: number; instance_type?: string } = {
        desired_capacity: capacity,
      };

      if (selectedInstanceType && selectedInstanceType !== asgStatus?.instance_type) {
        updateRequest.instance_type = selectedInstanceType;
      }

      await updateAsg(updateRequest);
      setSuccess('ASG updated successfully');
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update ASG');
    } finally {
      setUpdating(false);
    }
  };

  const handleQuickStart = () => {
    setDesiredCapacity(1);
    handleUpdate(1);
  };

  const handleQuickStop = () => {
    setDesiredCapacity(0);
    handleUpdate(0);
  };

  if (loading && !asgStatus) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2">
            GPU Instance Dashboard
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadStatus}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Status and Controls Row */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            {/* Status Overview */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Current Status
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Desired Capacity:
                  </Typography>
                  <Chip
                    label={asgStatus?.desired_capacity || 0}
                    color={asgStatus?.desired_capacity === 0 ? 'default' : 'primary'}
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Running Instances:
                  </Typography>
                  <Chip
                    label={asgStatus?.current_instances || 0}
                    color={asgStatus?.current_instances === 0 ? 'default' : 'success'}
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Instance Type:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {asgStatus?.instance_type || 'N/A'}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Min/Max:
                  </Typography>
                  <Typography variant="body2">
                    {asgStatus?.min_size || 0} / {asgStatus?.max_size || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Controls */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Controls
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Desired Capacity (0-8)</InputLabel>
                  <Select
                    value={desiredCapacity}
                    label="Desired Capacity (0-8)"
                    onChange={(e) => setDesiredCapacity(Number(e.target.value))}
                  >
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((cap) => (
                      <MenuItem key={cap} value={cap}>
                        {cap}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Instance Type</InputLabel>
                  <Select
                    value={selectedInstanceType}
                    label="Instance Type"
                    onChange={(e) => setSelectedInstanceType(e.target.value)}
                  >
                    {instanceTypes.map((type) => (
                      <MenuItem key={type.name} value={type.name}>
                        {type.name} - {type.gpu_type} ({type.vcpus} vCPU, {type.memory_gb}GB)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleUpdate()}
                    disabled={updating}
                    fullWidth
                  >
                    {updating ? <CircularProgress size={20} /> : 'Update ASG'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<StartIcon />}
                    onClick={handleQuickStart}
                    disabled={updating || desiredCapacity === 1}
                    size="small"
                  >
                    Start (1)
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={handleQuickStop}
                    disabled={updating || desiredCapacity === 0}
                    size="small"
                  >
                    Stop (0)
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Instance Details */}
          {asgStatus && asgStatus.instances.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Running Instances
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Instance ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell>Launch Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {asgStatus.instances.map((instance) => (
                      <TableRow key={instance.instance_id}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {instance.instance_id}
                          </Typography>
                        </TableCell>
                        <TableCell>{instance.instance_type}</TableCell>
                        <TableCell>
                          <Chip
                            label={instance.state}
                            color={instance.state === 'running' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(instance.launch_time).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

